import { useCallback, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader, IconButton } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import { getDeleteProfileErrorMessage } from "app/pages/sales-intelligence/modules/employee-profiles/utils/getDeleteProfileErrorMessage";
import { usePlaybooksTrackingService } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksTrackingService";
import { usePlaybookToasts } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybookToasts";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import RemoveProfileModal from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/components/RemoveProfileModal/RemoveProfileModal";
import { useDeleteContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useDeleteContactProfileMutation";
import { usePlaybookProfilesQuery } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/usePlaybookProfilesQuery";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import { usePlaybookLocationOptions } from "../../../../hooks/usePlaybookLocationOptions";

import { PlaybookContactsLimitFilterCard } from "./PlaybookContactsLimitFilterCard";
import { PlaybookEmployeeLocationFilterCard } from "./PlaybookEmployeeLocationFilterCard";
import { PlaybookProfileModalContainer } from "./PlaybookProfileModalContainer";
import { PlaybookProfileRow } from "./PlaybookProfileRow";

type PlaybookEmployeeProfilesStepProps = {
    isSharedWithAccount: boolean;
    enabledProfileIds: string[];
    contactsCount?: number;
    onContactsCountChange: (value?: number) => void;
    employeeLocation?: IEmployeeLocationFilterDto;
    onEmployeeLocationChange: (value?: IEmployeeLocationFilterDto) => void;
    onToggle: (profileId: string, enabled: boolean) => void;
    onProfileCreated: (profileId: string) => void;
    onProfileDeleted: (profileId: string) => void;
};

export const PlaybookEmployeeProfilesStep = ({
    isSharedWithAccount,
    enabledProfileIds,
    contactsCount,
    onContactsCountChange,
    employeeLocation,
    onEmployeeLocationChange,
    onToggle,
    onProfileCreated,
    onProfileDeleted,
}: PlaybookEmployeeProfilesStepProps) => {
    const { translate } = useTranslate();
    const playbooksTracking = usePlaybooksTrackingService();
    const { showError, showErrorMessage } = usePlaybookToasts();
    const { countries, regions } = usePlaybookLocationOptions();
    const { data, isLoading } = usePlaybookProfilesQuery(isSharedWithAccount);
    const profiles = data?.profiles ?? [];

    // Champion is treated like Admin for shared authoring, same convention as the Personas page.
    const canManageShared = salesSettingsHelper.isHighLevelAccount();

    const [profileForEdit, setProfileForEdit] = useState<ProfileDto | null>(null);
    const [isModalOpened, setModalOpened] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState<ProfileDto | null>(null);

    const { mutateAsync: deleteProfile, isPending: isDeleting } = useDeleteContactProfileMutation();

    const handleCreateClick = useCallback(() => {
        playbooksTracking.trackCreateProfile();
        setProfileForEdit(null);
        setModalOpened(true);
    }, [playbooksTracking]);

    const handleEdit = useCallback(
        (profile: ProfileDto) => {
            playbooksTracking.trackEditProfile();
            setProfileForEdit(profile);
            setModalOpened(true);
        },
        [playbooksTracking],
    );

    const handleDelete = useCallback(
        (profile: ProfileDto) => {
            playbooksTracking.trackDeleteProfileOpen();
            setProfileToDelete(profile);
        },
        [playbooksTracking],
    );

    const handleCloseModal = useCallback(() => {
        setModalOpened(false);
        setProfileForEdit(null);
    }, []);

    const handleDeleteCancel = useCallback(() => {
        playbooksTracking.trackDeleteProfileCancel();
        setProfileToDelete(null);
    }, [playbooksTracking]);

    const handleDeleteConfirm = useCallback(
        async (id: ProfileDto["id"]) => {
            playbooksTracking.trackDeleteProfileConfirm();

            try {
                await deleteProfile(id);
                onProfileDeleted(id);
                setProfileToDelete(null);
            } catch (error) {
                const serverMessage = await getDeleteProfileErrorMessage(error);

                // A rejected delete is an expected outcome rather than a fault, so it is not reported to Sentry.
                if (serverMessage) {
                    showErrorMessage(serverMessage);
                    return;
                }

                sentryErrorTrackerWithScope({
                    errorMessage: "Failed to delete employee profile from the playbook wizard",
                    e: error as Error,
                    tags: { component: "playbooks" },
                    params: { extra: { profileId: id } },
                });
                showError();
            }
        },
        [deleteProfile, onProfileDeleted, playbooksTracking, showError, showErrorMessage],
    );

    const handleToggle = useCallback(
        (profileId: string, enabled: boolean) => {
            playbooksTracking.trackToggleProfile(enabled);
            onToggle(profileId, enabled);
        },
        [playbooksTracking, onToggle],
    );

    return (
        <Stack
            gap="24px"
            data-automation="playbook-wizard-profiles-step"
        >
            <PlaybookContactsLimitFilterCard
                value={contactsCount}
                onChange={onContactsCountChange}
            />

            <PlaybookEmployeeLocationFilterCard
                value={employeeLocation}
                onChange={onEmployeeLocationChange}
                countries={countries}
                regions={regions}
            />

            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon["400"]}
                data-automation="playbook-wizard-profiles-step-subtitle"
            >
                {translate("si.playbooks.wizard.employee_profiles.subtitle")}
            </Text>

            {isLoading ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    minHeight="320px"
                    data-automation="playbook-profiles-list-loading"
                >
                    <CircularLoader isLarge />
                </Flex>
            ) : (
                <Stack
                    gap="0"
                    borderRadius="6px"
                    border={`1px solid ${colorsPalettes.carbon["50"]}`}
                >
                    {profiles.map((profile) => (
                        <PlaybookProfileRow
                            key={profile.id}
                            profile={profile}
                            isEnabled={enabledProfileIds.includes(profile.id)}
                            canManageShared={canManageShared}
                            onToggle={handleToggle}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                    <Box padding="12px 16px">
                        <IconButton
                            type="flat"
                            iconName="add"
                            iconSize="sm"
                            onClick={handleCreateClick}
                            data-automation="playbook-profile-create-another"
                        >
                            {translate("si.playbooks.wizard.employee_profiles.create_another")}
                        </IconButton>
                    </Box>
                </Stack>
            )}

            <PlaybookProfileModalContainer
                isSharedWithAccount={isSharedWithAccount}
                profileForEdit={profileForEdit}
                isModalOpened={isModalOpened}
                onClose={handleCloseModal}
                onProfileCreated={onProfileCreated}
                onProfileDeleted={onProfileDeleted}
            />

            {profileToDelete ? (
                <RemoveProfileModal
                    profile={profileToDelete}
                    isLoading={isDeleting}
                    isOpened
                    onSubmit={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            ) : null}
        </Stack>
    );
};
